import Navbar from "@/components/site/Navbar";
import { Seo } from "@/components/site/Seo";
import Footer from "@/components/site/Footer";

const lastUpdated = "February 2026";

const intro =
  "This Privacy Policy explains how Happy Tiddlers Ltd (“we”, “us”, “our”) collects, uses, stores and retains personal information when you interact with us via our website, booking software, or other communications.";

const sections = [
  {
    title: "Data Controller",
    body: "Happy Tiddlers Ltd is the Data Controller responsible for your personal data.",
  },
  {
    title: "Our Commitment to Data Protection",
    body: "Happy Tiddlers Ltd complies with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018. We only process personal data where we have a lawful basis to do so and where it is necessary to deliver our services.",
  },
  {
    title: "Our Website",
    body: "We collect very limited technical information from visitors to our website. No personal data is actively collected. Website statistics gathered by our hosting provider may include IP address and date of visit for security and analytics purposes.",
  },
  {
    title: "Our Booking Software",
    body: "We use secure third-party cloud-based booking software. Data collected may include:",
    bullets: [
      "Name",
      "Address",
      "Telephone number(s)",
      "Email address(es)",
      "Children’s names and dates of birth",
      "Relevant medical or health information (where applicable)",
    ],
    after: "We may contact you regarding booking confirmations and reminders, venue information and updates, health and safety information, payment receipts, newsletters, offers and promotions (where consent has been given), and invitations to future terms. Providing this information and consenting to contact is necessary for Happy Tiddlers to fulfil its contractual obligations.",
  },
  {
    title: "Data Relating to Children",
    body: "We only collect and process children’s personal data with the consent of a parent or legal guardian. This data is limited to what is necessary for the safe and effective delivery of our swimming lessons:",
    bullets: ["Child’s name", "Date of birth", "Relevant medical information (if applicable)"],
  },
  {
    title: "Special Category Data",
    body: "Medical and health information is classed as special category data under UK GDPR. We process this information only where necessary for health and safety purposes, and only with explicit consent from a parent or legal guardian.",
  },
  {
    title: "Card Payments and Purchases",
    body: "Happy Tiddlers Ltd is PCI compliant. We do not store debit or credit card details on site. Where card details are retained with consent, they are securely stored by authorised third-party payment providers. Card details are encrypted and inaccessible to Happy Tiddlers staff, except for the final four digits and expiry date.",
  },
  {
    title: "Lawful Basis for Processing Data",
    body: "We process personal data based on one or more of the following lawful bases:",
    bullets: [
      "Consent – where you have given clear permission",
      "Contract – where processing is necessary to provide our services",
      "Legitimate interests – where relevant and balanced against your rights",
    ],
  },
  {
    title: "Third-Party Processors",
    body: "We do not sell personal data or share it with third parties for marketing purposes. We use trusted third-party service providers (such as booking and payment systems) solely to deliver our services. All providers are required to meet appropriate data protection and security standards.",
  },
  {
    title: "International Data Transfers",
    body: "We do not transfer personal data outside the United Kingdom or the European Economic Area.",
  },
  {
    title: "Protecting Your Data",
    body: "Personal data is stored securely using password-protected systems. Access is restricted to authorised personnel only. We regularly review our security measures to ensure ongoing protection.",
  },
  {
    title: "Data Retention",
    body: "We retain personal data only for as long as necessary to provide our services or to comply with legal obligations. Financial records are retained for up to seven years in accordance with UK law. Retention periods are reviewed periodically to ensure data is not kept longer than necessary.",
  },
  {
    title: "Your Rights",
    body: "Under UK GDPR, you have the right to:",
    bullets: [
      "Access personal data we hold about you",
      "Request correction or deletion of your data",
      "Object to or restrict data processing",
      "Object to automated decision-making",
      "Request transfer of your data to another provider (data portability)",
    ],
    after: "You also have the right to lodge a complaint with the Information Commissioner’s Office (ICO). Further information is available at www.ico.org.uk. To exercise your rights or request further information, please contact hello@happytiddlers.co.uk. Happy Tiddlers Swim School Ltd (company no. 10740608).",
  },
];

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#334155] font-body" data-testid="privacy-page">
      <Seo
        title="Privacy Policy | Happy Tiddlers Swim School"
        description="How Happy Tiddlers Swim School collects, uses and protects your personal data."
        path="/privacy"
      />
      <Navbar />

      <section className="relative overflow-hidden pt-16 pb-12 md:pt-24 md:pb-14">
        <div className="blob absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-[#0EA5E9]/10" />
        <div className="container mx-auto px-6 md:px-12 relative max-w-3xl">
          <span className="text-sm text-[#0EA5E9] tracking-[0.2em] uppercase font-bold">Legal</span>
          <h1 className="mt-4 font-heading text-4xl md:text-5xl font-black tracking-tighter text-[#0F172A] leading-[1.05]">
            Privacy Policy
          </h1>
          <p className="mt-3 text-sm font-semibold text-[#64748B]">Last updated: {lastUpdated}</p>
          <p className="mt-6 text-lg text-[#334155] leading-relaxed">{intro}</p>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="container mx-auto px-6 md:px-12 max-w-3xl">
          <div className="space-y-8">
            {sections.map((s, i) => (
              <div key={s.title} className="bg-white rounded-[1.5rem] p-8 border border-slate-100 shadow-sm" data-testid={`privacy-section-${i}`}>
                <h2 className="font-heading text-xl font-semibold text-[#0F172A]">{s.title}</h2>
                <p className="mt-3 text-[#334155] leading-relaxed">{s.body}</p>
                {s.bullets && (
                  <ul className="mt-4 space-y-2">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3 text-[#334155]">
                        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#0EA5E9] shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {s.after && <p className="mt-4 text-[#334155] leading-relaxed">{s.after}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
